/**
 * focus-trap.ts — the shared, format-agnostic keyboard focus trap for the a11y-view
 * substrate (#14 LIFT).
 *
 * ONE Tab-cycle trap for every modal + BOTH screen-reader popouts (the classic pdf.js
 * `/editor` panel `screen-reader.ts` and the shared PPTX / DOCX / PDF mount
 * `a11y-view-mount.ts`). Both SR panels shipped with Escape-close but NO focus trap
 * (phase-1 gap 129/191): a keyboard user Tabbing inside an open panel could walk out
 * into the document behind it. Relocated here from `editor/modal-logic.ts` — the SR
 * panels are the third consumer, which forces the shared home (rule #11); every call
 * site imports `FocusTrap` from HERE, no delegating shim (A.7).
 *
 * The primitives (`saveFocus` / `trap` / `release` / `restoreFocus`) are the original
 * per-modal API the attestation + hotkey modals drive. `activate` / `deactivate` are a
 * convenience pair (save+trap / release+restore) and `trap` accepts an optional
 * `onEscape` hook, so a caller with no existing Escape handling gets close-on-Escape
 * for free; the SR panels keep their own Escape handling and adopt only the Tab trap.
 *
 * @module a11y-view/focus-trap
 */
const FOCUSABLE = 'a[href], button:not(:disabled), input:not(:disabled), select, textarea, [tabindex]:not([tabindex="-1"])';
/**
 * Per-surface Tab-key focus trap. Each instance carries its own independent focus
 * state. Usage: `saveFocus()` before opening, `trap(container)` after, `release()` +
 * `restoreFocus()` on close — or the `activate` / `deactivate` convenience pair.
 */
export class FocusTrap {
    _container = null;
    _handler = null;
    _preFocusEl = null;
    /** Save the currently focused element so it can be restored on close. */
    saveFocus() {
        this._preFocusEl = document.activeElement; // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    }
    /** Trap Tab-key navigation inside `container`; optionally close on Escape. */
    trap(container, opts = {}) {
        // Release any prior trap on this instance first.
        this.release();
        const focusable = () => Array.from(container.querySelectorAll(FOCUSABLE));
        this._handler = (ev) => {
            if (ev.key === "Escape" && opts.onEscape) {
                opts.onEscape();
                return;
            }
            if (ev.key !== "Tab")
                return;
            const items = focusable();
            if (!items.length)
                return;
            const first = items[0]; // WHY: items.length !== 0 check above
            const last = items[items.length - 1]; // WHY: items.length !== 0 check above
            if (ev.shiftKey) {
                if (document.activeElement === first) {
                    last.focus();
                    ev.preventDefault();
                } // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            }
            else {
                if (document.activeElement === last) {
                    first.focus();
                    ev.preventDefault();
                } // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            }
        };
        container.addEventListener("keydown", this._handler);
        this._container = container;
    }
    /** Remove the Tab-key listener. Does not restore focus. */
    release() {
        if (this._handler && this._container) {
            this._container.removeEventListener("keydown", this._handler);
            this._handler = null;
            this._container = null;
        }
    }
    /** Restore focus to the element saved by saveFocus(). */
    restoreFocus() {
        if (this._preFocusEl)
            this._preFocusEl.focus();
    }
    /** Convenience: saveFocus() + trap() in one call. */
    activate(container, opts = {}) {
        this.saveFocus();
        this.trap(container, opts);
    }
    /** Convenience: release() the trap + restoreFocus() to the pre-trap element. */
    deactivate() {
        this.release();
        this.restoreFocus();
    }
}
